import { useState, useEffect } from 'react';
import Header from './components/layout/Header';
import MobileBottomBar from './components/layout/MobileBottomBar';
import PerformantMemberList from './components/members/PerformantMemberList';
import MapContainer from './components/map/MapContainer';
import FilterModal from './components/search/FilterModal';
import LoadingSpinner from './components/common/LoadingSpinner';
import MessageManager from './components/common/MessageManager';
import AvailabilityCalendarModal from './components/common/AvailabilityCalendarModal';
import { useMembers } from './hooks/useMembers';
import { useFilters } from './hooks/useFilters';
import { useResponsive } from './hooks/useResponsive';
import { useMapSync } from './hooks/useMapSync';
import { useFavorites } from './hooks/useFavorites';

function App({ config }) {
    const sortOptions = config?.sortOptions || [];
    const showMap = config?.showMap !== false;
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [mobileView, setMobileView] = useState('list'); // 'list' or 'map'
    const [mobileSortBy, setMobileSortBy] = useState('joined');
    const [mobileViewMode, setMobileViewMode] = useState('compact');
    
    const { isMobile } = useResponsive();
    const { filters, updateFilter, clearFilters } = useFilters();
    const { 
        members, 
        loading, 
        error, 
        searchTerm, 
        setSearchTerm, 
        loadMembers 
    } = useMembers(config, filters);

    const {
        visibleMembers,
        handleMapBoundsChange,
        handleMemberHover,
        handleMemberHoverOut,
        handleMarkerHover,
        handleMarkerHoverOut,
        registerMapInstance,
        registerMarker,
        unregisterMarker,
        registerMemberList,
        updateVisibleMembers
    } = useMapSync(config.includeNoLocation);

    // Get current user ID from config
    const currentUserId = config?.currentUserId || null;

    const {
        loading: favoritesLoading,
        error: favoritesError,
        loadFavorites,
        toggleFavorite,
        isFavorite,
        isPending: isPendingFavorite,
        clearError: clearFavoritesError
    } = useFavorites(currentUserId);

    // Determine if user is logged in - user_id is 0 when not logged in
    const isLoggedIn = Boolean(currentUserId) && currentUserId !== '0' && currentUserId !== 0;

    useEffect(() => {
        // Check if filters are being applied (not just cleared)
        const hasActiveFilters = Object.keys(filters).length > 0;
        loadMembers(hasActiveFilters);
    }, [filters, searchTerm]);

    // Load favorites when currentUserId becomes available
    useEffect(() => {
        if (currentUserId) {
            loadFavorites();
        }
    }, [currentUserId, loadFavorites]);

    // Update visible members when members change or on initial load
    useEffect(() => {
        updateVisibleMembers(members, null); // null bounds means show all members initially
    }, [members, updateVisibleMembers]);

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleFilterChange = (newFilters) => {
        // Clear all filters first
        clearFilters();
        
        // Apply new filters - newFilters should be {fieldId: value}
        Object.keys(newFilters).forEach(fieldId => {
            if (newFilters[fieldId]) {
                updateFilter(fieldId, newFilters[fieldId]);
            }
        });
        
        setShowFilterModal(false);
    };

    const handleClearFilters = () => {
        clearFilters();
        setSearchTerm('');
    };

    const isValueActive = (value) => {
        if (Array.isArray(value)) {
            return value.length > 0;
        }

        if (value && typeof value === 'object') {
            return Object.values(value).some((nestedValue) => isValueActive(nestedValue));
        }

        if (typeof value === 'string') {
            return value.trim() !== '';
        }

        return Boolean(value);
    };

    const activeFilterCount = Object.values(filters).filter(isValueActive).length;
    const hasActiveFilters = activeFilterCount > 0;

    if (loading) {
        return (
            <div className="gw-bp-geosearch-container">
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="gw-bp-geosearch-container">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    return (
        <div className="gw-bp-geosearch-container">
            <div className={`gw-bp-geosearch-content ${isMobile ? 'mobile' : ''}`}>
                {isMobile ? (
                    // Mobile: Show either list or map based on toggle
                    <>
                        <Header 
                            searchTerm={searchTerm}
                            onSearch={handleSearch}
                            onShowFilters={() => setShowFilterModal(true)}
                            onClearFilters={handleClearFilters}
                            hasActiveFilters={hasActiveFilters}
                            sortBy={mobileSortBy}
                            onSortChange={setMobileSortBy}
                            sortOptions={sortOptions}
                            mobileView={mobileView}
                        />
                        {mobileView === 'list' || !showMap ? (
                            <PerformantMemberList 
                                members={members}
                                loading={loading}
                                config={config}
                                onMemberHover={handleMemberHover}
                                onMemberHoverOut={handleMemberHoverOut}
                                registerMemberList={registerMemberList}
                                isFavorite={isFavorite}
                                onToggleFavorite={toggleFavorite}
                                isPendingFavorite={isPendingFavorite}
                                isLoggedIn={isLoggedIn}
                                enableVirtualization={true}
                                performanceMode="auto"
                                sortBy={mobileSortBy}
                                onSortChange={setMobileSortBy}
                                sortOptions={sortOptions}
                                viewMode={mobileViewMode}
                                onViewModeChange={setMobileViewMode}
                            />
                        ) : (
                            <MapContainer 
                                members={members}
                                config={config}
                                height="calc(100vh - 176px)"
                                onMarkerHover={handleMarkerHover}
                                onMarkerHoverOut={handleMarkerHoverOut}
                                onBoundsChange={handleMapBoundsChange}
                                registerMapInstance={registerMapInstance}
                                registerMarker={registerMarker}
                                unregisterMarker={unregisterMarker}
                                isFavorite={isFavorite}
                                onToggleFavorite={toggleFavorite}
                                isPendingFavorite={isPendingFavorite}
                                isLoggedIn={isLoggedIn}
                            />
                        )}

                        <MobileBottomBar
                            mobileView={mobileView}
                            onMobileViewChange={setMobileView}
                            onShowFilters={() => setShowFilterModal(true)}
                            hasActiveFilters={hasActiveFilters}
                            activeFilterCount={activeFilterCount}
                            showClear={hasActiveFilters || Boolean(searchTerm)}
                            onClear={handleClearFilters}
                            showMap={showMap}
                        />
                    </>
                ) : (
                    // Desktop: Side-by-side layout
                    <>
                        <div className={`left-panel ${!showMap ? 'no-map' : ''}`}>
                            <Header 
                                searchTerm={searchTerm}
                                onSearch={handleSearch}
                                onShowFilters={() => setShowFilterModal(true)}
                                onClearFilters={handleClearFilters}
                                hasActiveFilters={hasActiveFilters}
                                sortBy={mobileSortBy}
                                onSortChange={setMobileSortBy}
                                sortOptions={sortOptions}
                            />
                            <PerformantMemberList 
                                members={showMap ? visibleMembers : members}
                                loading={loading}
                                config={config}
                                onMemberHover={handleMemberHover}
                                onMemberHoverOut={handleMemberHoverOut}
                                registerMemberList={registerMemberList}
                                isFavorite={isFavorite}
                                onToggleFavorite={toggleFavorite}
                                isPendingFavorite={isPendingFavorite}
                                isLoggedIn={isLoggedIn}
                                enableVirtualization={true}
                                performanceMode="auto"
                                showResultCount={true}
                                totalMembers={members.length}
                                sortOptions={sortOptions}
                            />
                        </div>
                        {showMap && (
                            <div className="right-panel">
                                <MapContainer 
                                    members={members}
                                    config={config}
                                    height="100%"
                                    onMarkerHover={handleMarkerHover}
                                    onMarkerHoverOut={handleMarkerHoverOut}
                                    onBoundsChange={(bounds) => handleMapBoundsChange(bounds, members)}
                                    registerMapInstance={registerMapInstance}
                                    registerMarker={registerMarker}
                                    unregisterMarker={unregisterMarker}
                                    isFavorite={isFavorite}
                                    onToggleFavorite={toggleFavorite}
                                    isPendingFavorite={isPendingFavorite}
                                    isLoggedIn={isLoggedIn}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {showFilterModal && (
                <FilterModal
                    config={config}
                    currentFilters={filters}
                    onApply={handleFilterChange}
                    onClose={() => setShowFilterModal(false)}
                />
            )}

            <MessageManager />
            {config.gwVisitsCalendarEnabled && (
                <AvailabilityCalendarModal restBase={config.gwVisitsRestBase} />
            )}
        </div>
    );
}

export default App;
